import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Wordmark } from "@/components/Logo";

export default function Login() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next"); // e.g. /insurance — deep-link after login
  const { login, verify2fa } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [challenge, setChallenge] = useState(""); // if set, show 2FA code screen
  const [code, setCode] = useState("");

  const dest = (user) => {
    if (next && next.startsWith("/")) return next;
    return user.role && user.role !== "member" ? "/admin" : "/dashboard";
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const r = await login(email, password);
    setBusy(false);
    if (r.ok) {
      toast.success("Welcome back, " + (r.user.full_name || r.user.email));
      nav(dest(r.user));
    } else if (r.requires_2fa) {
      setChallenge(r.challenge);
      setError("");
    } else {
      setError(r.error);
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const r = await verify2fa(challenge, code.trim());
    setBusy(false);
    if (r.ok) {
      if (r.used_backup) toast.warning("Backup code used — remember to regenerate your codes.");
      toast.success("Welcome back, " + (r.user.full_name || r.user.email));
      nav(dest(r.user));
    } else {
      setError(r.error);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card className="p-8">
            <div className="text-center mb-6">
              <Wordmark className="h-16 mx-auto mb-3" />
              <div className="text-xs uppercase tracking-widest text-brand-blue font-semibold">
                {challenge ? "Two-Factor Authentication" : "Member Login"}
              </div>
              <h1 className="text-3xl font-black tracking-tight mt-1">
                {challenge ? "Enter your 6-digit code" : "Welcome back"}
              </h1>
              <p className="text-sm text-muted-foreground mt-2">
                {challenge
                  ? "Open Google Authenticator (or your authenticator app) and enter the current code. You can also enter a backup code."
                  : "Sign in to your KELTA account."}
              </p>
            </div>
            {!challenge ? (
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email or Username</Label>
                  <Input
                    id="email" type="text" required value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com or username"
                    autoComplete="username"
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password" type="password" required value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    data-testid="login-password-input"
                  />
                </div>
                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 rounded-md p-2.5" data-testid="login-error">
                    {error}
                  </div>
                )}
                <Button
                  type="submit" disabled={busy}
                  className="w-full bg-brand-blue hover:bg-brand-blue/90 text-white h-11"
                  data-testid="login-submit-btn"
                >
                  {busy ? "Signing in…" : "Sign in"}
                </Button>
              </form>
            ) : (
              <form onSubmit={submit2fa} className="space-y-4">
                <div>
                  <Label htmlFor="tfa-code">Authenticator code</Label>
                  <Input
                    id="tfa-code" type="text" required value={code}
                    onChange={e => setCode(e.target.value)}
                    placeholder="123 456 or backup code"
                    autoFocus
                    autoComplete="one-time-code"
                    inputMode="text"
                    maxLength={16}
                    className="tracking-widest text-center text-lg font-mono"
                    data-testid="login-2fa-code-input"
                  />
                </div>
                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 rounded-md p-2.5" data-testid="login-2fa-error">
                    {error}
                  </div>
                )}
                <Button
                  type="submit" disabled={busy || !code}
                  className="w-full bg-brand-blue hover:bg-brand-blue/90 text-white h-11"
                  data-testid="login-2fa-submit-btn"
                >
                  {busy ? "Verifying…" : "Verify & continue"}
                </Button>
                <button
                  type="button"
                  onClick={() => { setChallenge(""); setCode(""); setError(""); }}
                  className="w-full text-xs text-muted-foreground hover:text-foreground"
                  data-testid="login-2fa-back-btn"
                >
                  ← Use a different account
                </button>
              </form>
            )}

            {!challenge && (
              <>
                <div className="mt-5 flex items-center gap-3 text-xs uppercase tracking-wider text-muted-foreground">
                  <span className="flex-1 h-px bg-border" />
                  or
                  <span className="flex-1 h-px bg-border" />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                    const redirectUrl = window.location.origin + "/dashboard";
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                  data-testid="google-signin-btn"
                  className="mt-4 w-full h-11 rounded-md border border-border bg-white text-foreground hover:bg-muted/30 transition-colors flex items-center justify-center gap-3 text-sm font-medium"
                >
                  <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                  Continue with Google
                </button>

                <div className="mt-6 text-center text-sm text-muted-foreground">
                  New here? <Link to="/register" className="text-brand-blue font-medium">Join KELTA</Link>
                </div>
              </>
            )}
          </Card>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
